import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer, Typography } from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  ScissorOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  PhoneOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import { STATUS_CONFIG } from './constants';
import GuestCustomerBadge from '../../shared/GuestCustomerBadge';
import { PaymentStatusBadge } from '../../shared/PaymentStatusBadge';
import { AttendanceStatusBadge } from '../../shared/AttendanceStatusBadge';
import type { Booking } from '../../../types/provider';

const { Text } = Typography;

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return iso.substring(11, 16);
}

function formatDateLong(iso: string, monthLabels: string[], weekdayLabels: string[]): string {
  const d = new Date(iso);
  const day = d.getDate();
  const month = monthLabels[d.getMonth()] ?? '';
  const weekday = weekdayLabels[d.getDay()] ?? '';
  return `${weekday}, ${day} ${month}`;
}

function isPhone(value: string): boolean {
  return /^[\d\s+\-()]{7,}$/.test(value.trim()) && /\d{7,}/.test(value.replace(/\D/g, ''));
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('998')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
  }
  if (digits.length === 9) {
    return `+998 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  }
  return raw;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── InfoRow ────────────────────────────────────────────────────────────────

function InfoRow({
  icon,
  iconColor,
  label,
  children,
}: {
  icon: ReactNode;
  iconColor: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--bl-surface, #fff)',
        borderRadius: 14,
        border: '1px solid var(--bl-divider, #ebebeb)',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: `linear-gradient(135deg, ${iconColor}14 0%, ${iconColor}24 100%)`,
          border: `1px solid ${iconColor}1a`,
          color: iconColor,
          fontSize: 15,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <Text
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--bl-text-muted, #9ca3af)',
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 4,
          }}
        >
          {label}
        </Text>
        {children}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface Props {
  booking: Booking | null;
  onClose: () => void;
}

export default function BookingDetailSheet({ booking: incomingBooking, onClose }: Props) {
  const { t } = useTranslation(['common', 'provider']);
  const isOpen = !!incomingBooking;
  // Keep last-known booking visible during close animation
  const [booking, setBooking] = useState<Booking | null>(incomingBooking);

  useEffect(() => {
    if (incomingBooking) setBooking(incomingBooking);
  }, [incomingBooking]);

  useEffect(() => {
    if (!isOpen) return;

    const handleBack = () => onClose();
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(handleBack);

    return () => {
      WebApp.BackButton.offClick(handleBack);
      WebApp.BackButton.hide();
    };
  }, [isOpen, onClose]);

  const handleAfterOpenChange = (visible: boolean) => {
    if (!visible) setBooking(null);
  };

  if (!booking) return null;

  const config = STATUS_CONFIG[booking.status] ?? STATUS_CONFIG['PENDING'];
  const statusLabel = t(`provider:bookings.statusLabels.${booking.status}`, {
    defaultValue: booking.status,
  });
  const isCanceled =
    booking.status === 'CANCELED_BY_USER' ||
    booking.status === 'CANCELED_BY_SERVICE_PROVIDER';
  const cancellationReasonDisplay = booking.cancellationReasonText ?? booking.cancellationReason;
  const isPending = booking.status === 'PENDING';

  const clientDisplay =
    booking.clientName && isPhone(booking.clientName)
      ? formatPhone(booking.clientName)
      : booking.clientName;
  const avatarLetters = clientDisplay ? getInitials(clientDisplay) : '?';

  // Build i18n labels for date formatting
  const monthLabels = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) =>
    t(`provider:bookings.dateHeader.months.${i}`),
  );
  const weekdayLabels = [0, 1, 2, 3, 4, 5, 6].map((i) =>
    t(`provider:bookings.dateHeader.weekdays.${i}`),
  );
  const dateLong = formatDateLong(booking.startTime, monthLabels, weekdayLabels);

  return (
    <Drawer
      open={isOpen}
      placement="bottom"
      onClose={onClose}
      closable={false}
      maskClosable
      height="auto"
      afterOpenChange={handleAfterOpenChange}
      styles={{
        body: {
          padding: 0,
          background: 'var(--bl-surface-soft, #f7f8fa)',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        },
        wrapper: {
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
        },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* ─── Drag handle ─────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          padding: '10px 0 8px',
          background: 'var(--bl-surface, #fff)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--bl-divider, #e5e7eb)',
            margin: '0 auto',
          }}
        />
      </div>

      {/* ─── Hero section ────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          position: 'relative',
          padding: '12px 20px 18px',
          background: `linear-gradient(135deg, ${config.color}10 0%, ${config.color}20 60%, ${config.color}08 100%)`,
          borderBottom: `1px solid ${config.color}14`,
          overflow: 'hidden',
        }}
      >
        {/* Decorative blur orb */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -50,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: `${config.color}24`,
            filter: 'blur(6px)',
            opacity: 0.5,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 14,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--bl-text-muted, #6b7280)',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            {t('provider:bookings.detail.title')}
          </Text>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {booking.createdByRole === 'SERVICE_PROVIDER' && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--bl-accent, #1677ff)',
                  background: 'var(--bl-accent-soft, #e7f1fe)',
                  borderRadius: 12,
                  padding: '2px 8px',
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                }}
              >
                {t('provider:bookings.detail.walkInBadge')}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common:actions.close', { defaultValue: 'Close' })}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(255,255,255,0.85)',
                color: 'var(--bl-text-muted, #6b7280)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Time + status */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--bl-text-muted, #6b7280)',
                display: 'block',
                marginBottom: 2,
              }}
            >
              {dateLong}
            </Text>
            <Text
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.6,
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
                display: 'block',
              }}
            >
              {formatTime(booking.startTime)}
              <span
                style={{
                  fontWeight: 500,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  margin: '0 6px',
                  fontSize: 22,
                }}
              >
                –
              </span>
              {formatTime(booking.endTime)}
            </Text>
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: config.color,
              background: 'rgba(255,255,255,0.85)',
              border: `1px solid ${config.color}30`,
              borderRadius: 20,
              padding: '5px 12px',
              flexShrink: 0,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: config.color,
                boxShadow: isPending ? `0 0 0 3px ${config.color}24` : 'none',
              }}
            />
            {statusLabel}
          </div>
        </div>
      </div>

      {/* ─── Scrollable content ──────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {/* Client */}
        {clientDisplay && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background: 'var(--bl-surface, #fff)',
              borderRadius: 14,
              border: '1px solid var(--bl-divider, #ebebeb)',
            }}
          >
            {/* Avatar */}
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${config.color}18 0%, ${config.color}30 100%)`,
                border: `1px solid ${config.color}1a`,
                color: config.color,
                fontSize: 15,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                letterSpacing: -0.2,
              }}
            >
              {avatarLetters || <UserOutlined />}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 2,
                }}
              >
                {t('provider:bookings.detail.clientLabel')}
              </Text>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.25,
                }}
              >
                {clientDisplay}
              </Text>
              {booking.clientPhone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <PhoneOutlined
                    style={{ fontSize: 10, color: 'var(--bl-text-muted, #9ca3af)' }}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      color: 'var(--bl-text-muted, #6b7280)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {isPhone(booking.clientPhone) ? formatPhone(booking.clientPhone) : booking.clientPhone}
                  </Text>
                </div>
              )}
              {(booking.customerType === 'GUEST' || booking.customerAttendanceStatus) && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginTop: 8,
                    alignItems: 'center',
                  }}
                >
                  {booking.customerType === 'GUEST' && <GuestCustomerBadge />}
                  {booking.customerAttendanceStatus && (
                    <AttendanceStatusBadge
                      status={booking.customerAttendanceStatus}
                      size="sm"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Service */}
        {booking.serviceName && (
          <InfoRow
            icon={<ScissorOutlined />}
            iconColor="#1677ff"
            label={t('provider:bookings.detail.serviceLabel')}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--bl-text-strong, #0f172a)',
                display: 'block',
                lineHeight: 1.3,
              }}
            >
              {booking.serviceName}
            </Text>
            {booking.durationMinutes && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                <ClockCircleOutlined
                  style={{ fontSize: 10, color: 'var(--bl-text-muted, #9ca3af)' }}
                />
                <Text
                  style={{
                    fontSize: 12,
                    color: 'var(--bl-text-muted, #6b7280)',
                  }}
                >
                  {t('common:units.minutes', { count: booking.durationMinutes })}
                </Text>
              </div>
            )}
          </InfoRow>
        )}

        {/* Price */}
        {booking.totalAmount !== null && booking.totalAmount !== undefined && (
          <InfoRow
            icon={<DollarOutlined />}
            iconColor={booking.totalAmount > 0 ? '#1f8b4c' : '#8c8c8c'}
            label={t('provider:bookings.detail.priceLabel')}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: 'var(--bl-text-strong, #0f172a)',
                  letterSpacing: -0.4,
                  lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {booking.totalAmount.toLocaleString('uz-UZ')}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--bl-text-muted, #9ca3af)',
                }}
              >
                {t('provider:common.currency')}
              </Text>
            </div>
            {booking.totalAmount > 0 && (
              <div style={{ marginTop: 8 }}>
                <PaymentStatusBadge status={booking.paymentStatus} size="sm" />
              </div>
            )}
          </InfoRow>
        )}

        {/* Notes */}
        {booking.customerNotes && (
          <InfoRow
            icon={<FileTextOutlined />}
            iconColor="#9254de"
            label={t('provider:bookings.detail.notesLabel')}
          >
            <Text
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--bl-text-strong, #0f172a)',
                display: 'block',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {booking.customerNotes}
            </Text>
          </InfoRow>
        )}

        {/* Cancellation reason */}
        {isCanceled && cancellationReasonDisplay && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '12px 14px',
              background: 'rgba(255,77,79,0.06)',
              borderRadius: 14,
              border: '1px solid rgba(255,77,79,0.22)',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 11,
                background:
                  'linear-gradient(135deg, rgba(255,77,79,0.10) 0%, rgba(255,77,79,0.22) 100%)',
                border: '1px solid rgba(255,77,79,0.16)',
                color: '#cf1322',
                fontSize: 15,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CloseCircleOutlined />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#cf1322',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 4,
                }}
              >
                {t('provider:bookings.detail.cancellationReasonLabel')}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--bl-text-strong, #111)',
                  display: 'block',
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {cancellationReasonDisplay}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
